/**
 * GridRenderer.ts
 * Renders the octile grid in 3D using Babylon.js.
 *
 * CORRECTED GEOMETRY:
 * - Octagons share LONG edges with adjacent octagons (N/E/S/W)
 * - Square connectors sit at DIAGONAL intersections as 45°-rotated diamonds
 * - Connectors fill the cut-corner gaps between 4 octagons
 */

import {
    Scene,
    Mesh,
    StandardMaterial,
    DynamicTexture,
    Texture,
    Color3,
    Vector3,
    VertexData
} from '@babylonjs/core'
import type { GridCell, OctileCell, SquareConnector, CellType, CellTheme, WallState } from '../types/OctileTypes.ts'
import { gridToWorld, getCellType, gridKey } from './GridAddressing.ts'
import { CELL_TYPE_VISUALS, THEME_VISUALS, getCellGlyph } from './CellVisuals.ts'
import { getFloorTexturePath, getWallTexturePath } from './TextureCatalog.ts'

// ─── Color Helpers (derived from CellVisuals registry) ──────

export const THEME_FLOOR_COLORS: Record<CellTheme, Color3> = {} as any
for (const [theme, v] of Object.entries(THEME_VISUALS)) {
    (THEME_FLOOR_COLORS as any)[theme] = new Color3(v.rgb[0], v.rgb[1], v.rgb[2])
}

export const CELL_TYPE_COLORS: Partial<Record<CellType, Color3>> = {}
for (const [type, v] of Object.entries(CELL_TYPE_VISUALS)) {
    (CELL_TYPE_COLORS as any)[type] = new Color3(v.rgb[0], v.rgb[1], v.rgb[2])
}

const CONNECTOR_FLOOR_COLOR = new Color3(0.28, 0.24, 0.20)

// ─── Grid Renderer ──────────────────────────────────────────

interface CellMeshes {
    floor: Mesh
    walls: Mesh[]
    glyph?: Mesh
}

export class GridRenderer {
    private scene: Scene
    private cellMeshes: Map<string, CellMeshes> = new Map()
    private selectedMeshes: Set<Mesh> = new Set()
    private wallHeight = 4
    private textureCache: Map<string, Texture> = new Map()
    private currentGrid: GridCell[][] | null = null

    constructor(scene: Scene) {
        this.scene = scene
    }

    /** Load or retrieve cached Babylon Texture. Tiles at 1× across each cell. */
    private getTexture(path: string): Texture {
        let tex = this.textureCache.get(path)
        if (!tex) {
            tex = new Texture(path, this.scene, false, true, Texture.BILINEAR_SAMPLINGMODE)
            tex.wrapU = Texture.WRAP_ADDRESSMODE
            tex.wrapV = Texture.WRAP_ADDRESSMODE
            this.textureCache.set(path, tex)
        }
        return tex
    }

    renderGrid(
        grid: GridCell[][],
        baseSquareSize: number = 12.8,
        cornerCutRatio: number = 0.20
    ): void {
        this.clear()
        this.currentGrid = grid

        for (let row = 0; row < grid.length; row++) {
            for (let col = 0; col < grid[row].length; col++) {
                const cell = grid[row][col]
                if (!cell?.active) continue

                const worldPos = gridToWorld(row, col, baseSquareSize, cornerCutRatio)
                const kind = getCellType(row, col)

                if (kind === 'octagon' && cell.kind === 'octagon') {
                    this.renderOctagonCell(cell, worldPos, baseSquareSize, cornerCutRatio)
                } else if (kind === 'connector' && cell.kind === 'connector') {
                    this.renderConnectorDiamond(cell, worldPos, baseSquareSize, cornerCutRatio, grid)
                }
            }
        }
    }

    // ─── Octagon Rendering ──────────────────────────────

    private renderOctagonCell(
        cell: OctileCell,
        worldPos: { x: number; y: number; z: number },
        baseSize: number,
        cutRatio: number
    ): void {
        const key = gridKey(cell.row, cell.col)
        const cut = baseSize * cutRatio
        const half = baseSize / 2
        const vertices = this.getOctagonVertices(half, cut)

        // Floor
        const floor = this.createPolygonMesh(`oct_floor_${key}`, vertices, worldPos)
        const floorColor = CELL_TYPE_COLORS[cell.type] ?? THEME_FLOOR_COLORS[cell.theme] ?? THEME_FLOOR_COLORS.natural_cave
        const floorTexPath = getFloorTexturePath(cell.type, cell.theme, cell.row, cell.col)
        const floorMat = new StandardMaterial(`oct_fmat_${key}`, this.scene)
        floorMat.diffuseTexture = this.getTexture(floorTexPath)
        floorMat.diffuseColor = floorColor   // tints the texture with cell type color
        floorMat.specularColor = new Color3(0.05, 0.05, 0.05)
        floor.material = floorMat
        floor.isPickable = true
        floor.metadata = { gridRow: cell.row, gridCol: cell.col, cellKind: 'octagon' }

        // Glyph overlay (white bold stamp on transparent background)
        const glyph = getCellGlyph(cell.type)
        let glyphMesh: Mesh | undefined
        if (glyph) {
            glyphMesh = this.createPolygonMesh(`oct_glyph_${key}`, vertices, {
                x: worldPos.x, y: 0.03, z: worldPos.z  // just above floor
            })
            const glyphMat = this.createGlyphOverlay(`oct_gmat_${key}`, glyph)
            glyphMesh.material = glyphMat
            glyphMesh.isPickable = false
        }

        // 8 Wall segments (skip duplicate on shared long edges to prevent z-fighting)
        const walls: Mesh[] = []
        for (let i = 0; i < 8; i++) {
            // Long edges (0=N, 2=E, 4=S, 6=W) are shared with an adjacent octagon.
            // Only render from the cell with the lower grid key to avoid z-fighting.
            if (i % 2 === 0 && this.shouldSkipSharedWall(cell.row, cell.col, i)) {
                // Create invisible placeholder to keep wall indexing consistent
                const placeholder = new Mesh(`oct_w_${key}_${i}_skip`, this.scene)
                placeholder.isVisible = false
                placeholder.isPickable = true
                placeholder.metadata = { gridRow: cell.row, gridCol: cell.col, cellKind: 'octagon', wallIndex: i }
                walls.push(placeholder)
                continue
            }

            const wallMesh = this.createWallSegment(
                `oct_w_${key}_${i}`,
                vertices[i], vertices[(i + 1) % 8],
                worldPos, this.wallHeight
            )
            wallMesh.material = this.getWallMaterial(`oct_wm_${key}_${i}`, cell.walls[i], cell.type, cell.theme, cell.row, cell.col, i)
            wallMesh.isPickable = true
            wallMesh.metadata = { gridRow: cell.row, gridCol: cell.col, cellKind: 'octagon', wallIndex: i }
            walls.push(wallMesh)
        }

        this.cellMeshes.set(key, { floor, walls, glyph: glyphMesh })
    }

    // ─── Connector Diamond Rendering ────────────────────

    private renderConnectorDiamond(
        cell: SquareConnector,
        worldPos: { x: number; y: number; z: number },
        baseSize: number,
        cutRatio: number,
        grid: GridCell[][]
    ): void {
        const key = gridKey(cell.row, cell.col)
        // Diamond vertices must coincide with octagon cut-corner vertices.
        // Each octagon cuts corners by 'cut' amount → gap vertices sit at
        // distance 'cut' from the connector center, not cut*sqrt(2)/2.
        const connHalf = baseSize * cutRatio

        // Diamond vertices (square rotated 45°)
        const vertices = [
            { x: 0,         z: -connHalf },  // top
            { x: connHalf,  z: 0 },          // right
            { x: 0,         z: connHalf },   // bottom
            { x: -connHalf, z: 0 },          // left
        ]

        // Resolve connector's theme from first active diagonal neighbor
        const connTheme = this.getConnectorTheme(cell, grid)

        // Floor (diamond) — always textured, blended with adjacent room colors
        const floor = this.createPolygonMesh(`conn_floor_${key}`, vertices, worldPos)
        const floorMat = new StandardMaterial(`conn_fmat_${key}`, this.scene)
        const texPath = getFloorTexturePath('passage', connTheme, cell.row, cell.col)
        floorMat.diffuseTexture = this.getTexture(texPath)
        floorMat.diffuseColor = this.getConnectorBlendColor(cell, grid)
        floorMat.specularColor = new Color3(0.05, 0.05, 0.05)
        floor.material = floorMat
        floor.isPickable = true
        floor.metadata = { gridRow: cell.row, gridCol: cell.col, cellKind: 'connector' }

        // Connector walls: skip edges where the adjacent octagon is active
        // (its short-edge wall already covers that exact position — they share
        // the same vertices). Keep walls facing the void as boundary barriers.
        // Geometric edges: vertex[i]→vertex[(i+1)%4] face outward as:
        //   edge 0 (N→E) faces NE,  edge 1 (E→S) faces SE,
        //   edge 2 (S→W) faces SW,  edge 3 (W→N) faces NW
        const connNeighborOffsets: [number, number][] = [
            [-1, 1],   // edge 0 (N→E) faces NE octagon at (r-1, c+1)
            [1, 1],    // edge 1 (E→S) faces SE octagon at (r+1, c+1)
            [1, -1],   // edge 2 (S→W) faces SW octagon at (r+1, c-1)
            [-1, -1],  // edge 3 (W→N) faces NW octagon at (r-1, c-1)
        ]
        const walls: Mesh[] = []
        for (let i = 0; i < 4; i++) {
            const [dr, dc] = connNeighborOffsets[i]
            const neighbor = grid[cell.row + dr]?.[cell.col + dc]
            const neighborActive = neighbor?.active && neighbor.kind === 'octagon'

            if (neighborActive || cell.walls[i] === 'NONE') {
                const placeholder = new Mesh(`conn_w_${key}_${i}_skip`, this.scene)
                placeholder.isVisible = false
                placeholder.isPickable = true
                placeholder.metadata = { gridRow: cell.row, gridCol: cell.col, cellKind: 'connector', wallIndex: i }
                walls.push(placeholder)
                continue
            }

            const wallMesh = this.createWallSegment(
                `conn_w_${key}_${i}`,
                vertices[i], vertices[(i + 1) % 4],
                worldPos, this.wallHeight
            )
            wallMesh.material = this.getWallMaterial(`conn_wm_${key}_${i}`, cell.walls[i], 'empty', connTheme, cell.row, cell.col, i)
            walls.push(wallMesh)
        }

        this.cellMeshes.set(key, { floor, walls })
    }

    // ─── Selection ──────────────────────────────────────

    selectCell(row: number, col: number): void {
        const key = gridKey(row, col)
        const meshes = this.cellMeshes.get(key)
        if (!meshes) return

        const mat = meshes.floor.material as StandardMaterial
        if (mat) {
            mat.emissiveColor = new Color3(0.4, 0.3, 0.1)
        }

        this.selectedMeshes.add(meshes.floor)
    }

    deselectAll(): void {
        for (const mesh of this.selectedMeshes) {
            const mat = mesh.material as StandardMaterial
            if (mat) {
                mat.emissiveColor = Color3.Black()
            }
        }
        this.selectedMeshes.clear()
    }

    // ─── Material Helper ────────────────────────────────

    private getWallMaterial(
        name: string, state: WallState,
        cellType: CellType, theme: CellTheme,
        row: number, col: number, wallIndex: number
    ): StandardMaterial {
        const mat = new StandardMaterial(name, this.scene)
        mat.backFaceCulling = false

        // Texture lookup (returns null for OPEN)
        const texPath = getWallTexturePath(state, cellType, theme, row, col, wallIndex)
        if (texPath) {
            mat.diffuseTexture = this.getTexture(texPath)
        }

        switch (state) {
            case 'NONE':
                mat.alpha = 0
                mat.diffuseColor = Color3.Black()
                break
            case 'OPEN':
                mat.diffuseColor = new Color3(0.2, 0.2, 0.2)
                mat.alpha = 0.04
                break
            case 'DOOR':
                mat.diffuseColor = new Color3(0.85, 0.70, 0.50)  // warm wood tint
                mat.emissiveColor = new Color3(0.08, 0.05, 0.02)
                mat.alpha = 0.90
                break
            case 'GATE':
                mat.diffuseColor = new Color3(0.75, 0.75, 0.80)  // cool iron tint
                mat.emissiveColor = new Color3(0.05, 0.05, 0.07)
                mat.alpha = 0.85
                break
            case 'GRATE':
            case 'WINDOW':
                mat.diffuseColor = new Color3(0.65, 0.65, 0.70)
                mat.emissiveColor = new Color3(0.04, 0.04, 0.05)
                mat.alpha = 0.60
                break
            case 'SECRET':
                // Looks like a solid wall — same brightness
                mat.diffuseColor = new Color3(0.85, 0.80, 0.72)
                mat.emissiveColor = new Color3(0.06, 0.05, 0.04)
                break
            default:
                // SOLID — let texture show through at near-full brightness
                mat.diffuseColor = new Color3(0.85, 0.80, 0.72)
                mat.emissiveColor = new Color3(0.06, 0.05, 0.04)
        }

        return mat
    }

    // ─── Shared Wall Dedup ──────────────────────────────

    /** For long-edge walls shared between two octagons, skip if neighbor has lower key */
    private shouldSkipSharedWall(row: number, col: number, wallIndex: number): boolean {
        if (!this.currentGrid) return false

        // Neighbor octagon offsets for long edges: N(-2,0), E(0,+2), S(+2,0), W(0,-2)
        const offsets: Record<number, [number, number]> = {
            0: [-2, 0], 2: [0, 2], 4: [2, 0], 6: [0, -2]
        }
        const offset = offsets[wallIndex]
        if (!offset) return false

        const nr = row + offset[0]
        const nc = col + offset[1]
        const neighbor = this.currentGrid[nr]?.[nc]
        if (!neighbor?.active || neighbor.kind !== 'octagon') return false

        // Skip if neighbor has lower key (it renders the wall instead)
        return (nr * 1000 + nc) < (row * 1000 + col)
    }

    // ─── Connector Blend ──────────────────────────────

    /** Get the theme of the first active diagonal octagon neighbor */
    private getConnectorTheme(cell: SquareConnector, grid: GridCell[][]): CellTheme {
        const offsets = [[-1, -1], [-1, 1], [1, -1], [1, 1]]
        for (const [dr, dc] of offsets) {
            const neighbor = grid[cell.row + dr]?.[cell.col + dc]
            if (neighbor?.active && neighbor.kind === 'octagon') {
                return (neighbor as OctileCell).theme
            }
        }
        return 'natural_cave'
    }

    /** Average the floor colors of the 4 diagonal octagon neighbors */
    private getConnectorBlendColor(cell: SquareConnector, grid: GridCell[][]): Color3 {
        const offsets = [[-1, -1], [-1, 1], [1, -1], [1, 1]]
        let r = 0, g = 0, b = 0, count = 0

        for (const [dr, dc] of offsets) {
            const nr = cell.row + dr
            const nc = cell.col + dc
            const neighbor = grid[nr]?.[nc]
            if (neighbor?.active && neighbor.kind === 'octagon') {
                const oct = neighbor as OctileCell
                const c = CELL_TYPE_COLORS[oct.type] ?? THEME_FLOOR_COLORS[oct.theme] ?? THEME_FLOOR_COLORS.natural_cave
                r += c.r; g += c.g; b += c.b; count++
            }
        }

        if (count === 0) return CONNECTOR_FLOOR_COLOR

        // Slightly darken the average (~85%) so it reads as "floor between cells"
        const dim = 0.85
        return new Color3((r / count) * dim, (g / count) * dim, (b / count) * dim)
    }

    // ─── Glyph Overlay (transparent background, white bold glyph) ──

    private createGlyphOverlay(name: string, glyph: string): StandardMaterial {
        const size = 256
        const tex = new DynamicTexture(`${name}_tex`, size, this.scene, true)
        tex.hasAlpha = true
        const ctx = tex.getContext() as unknown as CanvasRenderingContext2D

        // Transparent background
        ctx.clearRect(0, 0, size, size)

        // White bold glyph with slight shadow for readability
        ctx.shadowColor = 'rgba(0,0,0,0.7)'
        ctx.shadowBlur = 8
        ctx.shadowOffsetX = 2
        ctx.shadowOffsetY = 2
        ctx.fillStyle = 'rgba(255,255,255,0.85)'
        ctx.font = 'bold 96px sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(glyph, size / 2, size / 2)

        tex.update()

        const mat = new StandardMaterial(name, this.scene)
        mat.diffuseTexture = tex
        mat.diffuseTexture.hasAlpha = true
        mat.useAlphaFromDiffuseTexture = true
        mat.emissiveColor = new Color3(0.9, 0.9, 0.9)  // glyphs glow slightly
        mat.disableLighting = true
        mat.zOffset = -1  // render above floor
        return mat
    }

    // ─── Geometry Helpers ───────────────────────────────

    /**
     * Octagon vertices (clockwise from top-left of N edge).
     * Cut corners create the short edges where diamonds connect.
     */
    private getOctagonVertices(half: number, cut: number): Array<{ x: number; z: number }> {
        return [
            { x: -half + cut, z: -half },        // N-left
            { x: half - cut,  z: -half },         // N-right
            { x: half,        z: -half + cut },   // E-top (start of NE cut)
            { x: half,        z: half - cut },    // E-bottom (end of SE cut)
            { x: half - cut,  z: half },          // S-right
            { x: -half + cut, z: half },          // S-left
            { x: -half,       z: half - cut },    // W-bottom
            { x: -half,       z: -half + cut },   // W-top
        ]
    }

    private createPolygonMesh(
        name: string,
        verts: Array<{ x: number; z: number }>,
        center: { x: number; y: number; z: number }
    ): Mesh {
        const positions: number[] = [0, 0, 0]  // center vertex
        for (const v of verts) positions.push(v.x, 0, v.z)

        const indices: number[] = []
        for (let i = 0; i < verts.length; i++) {
            indices.push(0, i + 1, ((i + 1) % verts.length) + 1)
        }

        // Compute bounding box for UV mapping
        let minX = 0, maxX = 0, minZ = 0, maxZ = 0
        for (const v of verts) {
            minX = Math.min(minX, v.x); maxX = Math.max(maxX, v.x)
            minZ = Math.min(minZ, v.z); maxZ = Math.max(maxZ, v.z)
        }
        const rangeX = maxX - minX || 1
        const rangeZ = maxZ - minZ || 1

        // UVs: center at (0.5, 0.5), edges mapped to 0–1
        const uvs: number[] = [0.5, 0.5]  // center
        for (const v of verts) {
            uvs.push((v.x - minX) / rangeX, (v.z - minZ) / rangeZ)
        }

        const normals: number[] = []
        const mesh = new Mesh(name, this.scene)
        const vd = new VertexData()
        vd.positions = positions
        vd.indices = indices
        vd.uvs = uvs
        VertexData.ComputeNormals(positions, indices, normals)
        vd.normals = normals
        vd.applyToMesh(mesh)

        mesh.position = new Vector3(center.x, 0.01, center.z)
        return mesh
    }

    private createWallSegment(
        name: string,
        a: { x: number; z: number },
        b: { x: number; z: number },
        center: { x: number; y: number; z: number },
        height: number
    ): Mesh {
        const WALL_HALF_THICK = 0.12

        // Perpendicular normal for wall thickness
        const dx = b.x - a.x
        const dz = b.z - a.z
        const len = Math.sqrt(dx * dx + dz * dz) || 1
        const nx = (-dz / len) * WALL_HALF_THICK
        const nz = (dx / len) * WALL_HALF_THICK

        // 8 vertices: outer face + inner face
        const positions = [
            // Outer face
            a.x + nx, 0, a.z + nz,           // 0
            b.x + nx, 0, b.z + nz,           // 1
            b.x + nx, height, b.z + nz,      // 2
            a.x + nx, height, a.z + nz,      // 3
            // Inner face
            a.x - nx, 0, a.z - nz,           // 4
            b.x - nx, 0, b.z - nz,           // 5
            b.x - nx, height, b.z - nz,      // 6
            a.x - nx, height, a.z - nz,      // 7
        ]

        const indices = [
            // Front (outer)
            0, 1, 2, 0, 2, 3,
            // Back (inner)
            5, 4, 7, 5, 7, 6,
            // Top cap
            3, 2, 6, 3, 6, 7,
            // Left side
            4, 0, 3, 4, 3, 7,
            // Right side
            1, 5, 6, 1, 6, 2,
        ]

        // UVs: map texture across wall face (u=along edge, v=up height)
        const uvs = [
            // Outer face: bottom-left, bottom-right, top-right, top-left
            0, 0,  1, 0,  1, 1,  0, 1,
            // Inner face: same layout (mirrored)
            1, 0,  0, 0,  0, 1,  1, 1,
        ]

        const normals: number[] = []
        const mesh = new Mesh(name, this.scene)
        const vd = new VertexData()
        vd.positions = positions
        vd.indices = indices
        vd.uvs = uvs
        VertexData.ComputeNormals(positions, indices, normals)
        vd.normals = normals
        vd.applyToMesh(mesh)

        mesh.position = new Vector3(center.x, 0, center.z)
        mesh.isPickable = true
        return mesh
    }

    // ─── Wall Update (single wall, no re-render) ────────

    updateWall(row: number, col: number, wallIndex: number, state: WallState): void {
        const key = gridKey(row, col)
        const meshes = this.cellMeshes.get(key)
        if (!meshes) return

        const wallMesh = meshes.walls[wallIndex]
        if (!wallMesh) return

        // Resolve cell type/theme for texture lookup
        const cell = this.currentGrid?.[row]?.[col]
        const cellType: CellType = (cell?.kind === 'octagon' ? (cell as OctileCell).type : 'empty')
        const theme: CellTheme = (cell?.kind === 'octagon' ? (cell as OctileCell).theme : 'natural_cave')

        const oldMat = wallMesh.material as StandardMaterial | null
        oldMat?.dispose()
        wallMesh.material = this.getWallMaterial(wallMesh.name + '_u', state, cellType, theme, row, col, wallIndex)
    }

    // ─── Floor Color Update ──────────────────────────

    updateFloorColor(row: number, col: number, color: Color3): void {
        const key = gridKey(row, col)
        const meshes = this.cellMeshes.get(key)
        if (!meshes) return

        const mat = meshes.floor.material as StandardMaterial
        if (mat) mat.diffuseColor = color
    }

    // ─── Wall Hover Highlight ──────────────────────────

    private highlightedWall: Mesh | null = null
    private highlightedWallOrigColor: Color3 | null = null

    highlightWall(row: number, col: number, wallIndex: number): void {
        this.clearWallHighlight()

        const key = gridKey(row, col)
        const meshes = this.cellMeshes.get(key)
        if (!meshes) return

        const wallMesh = meshes.walls[wallIndex]
        if (!wallMesh) return

        const mat = wallMesh.material as StandardMaterial
        if (mat) {
            this.highlightedWall = wallMesh
            this.highlightedWallOrigColor = mat.emissiveColor.clone()
            mat.emissiveColor = new Color3(0.1, 0.3, 0.5)
        }
    }

    clearWallHighlight(): void {
        if (this.highlightedWall) {
            const mat = this.highlightedWall.material as StandardMaterial
            if (mat && this.highlightedWallOrigColor) {
                mat.emissiveColor = this.highlightedWallOrigColor
            }
            this.highlightedWall = null
            this.highlightedWallOrigColor = null
        }
    }

    clear(): void {
        for (const meshes of this.cellMeshes.values()) {
            meshes.floor.dispose()
            meshes.glyph?.dispose()
            for (const wall of meshes.walls) wall.dispose()
        }
        this.cellMeshes.clear()
        this.selectedMeshes.clear()
        for (const tex of this.textureCache.values()) tex.dispose()
        this.textureCache.clear()
        this.currentGrid = null
    }

    dispose(): void { this.clear() }
}
